import {
  AMORTIZATION_LEVEL1,
  AMORTIZATION_LEVEL2,
  DEPOSIT_TO_VALUE_RATIO_LIMIT_AMORTIZATION_LEVEL1,
  DEPOSIT_TO_VALUE_RATIO_LIMIT_AMORTIZATION_LEVEL2,
  MAX_FULL_DEDUCTABLE_AMOUNT_PER_MONTH,
} from '../appConfig'

export function getDepositToValueRatio(
  downPayment: number,
  propertyValue: number
) {
  if (downPayment <= 0) return 0
  return (downPayment / propertyValue) * 100 || 0
}

export function getMonthlyAmortization(
  propertyValue: number,
  mortgage: number
) {
  const downPayment = propertyValue - mortgage

  if (
    getDepositToValueRatio(downPayment, propertyValue) <
    DEPOSIT_TO_VALUE_RATIO_LIMIT_AMORTIZATION_LEVEL2
  ) {
    return Math.round((mortgage * AMORTIZATION_LEVEL2) / 12)
  }

  if (
    getDepositToValueRatio(downPayment, propertyValue) <
    DEPOSIT_TO_VALUE_RATIO_LIMIT_AMORTIZATION_LEVEL1
  ) {
    return Math.round((mortgage * AMORTIZATION_LEVEL1) / 12)
  }

  return 0
}

export function getMonthlyInterestCost(
  interestRate: number,
  loanAmount: number
) {
  return Math.round((interestRate * loanAmount) / 100 / 12)
}

export function getMonthlyInterestCostWithTaxDeduction(
  interestRate: number,
  loanAmount: number
) {
  const interestCostPerMonth = getMonthlyInterestCost(interestRate, loanAmount)
  if (interestCostPerMonth > MAX_FULL_DEDUCTABLE_AMOUNT_PER_MONTH) {
    const amountAboveMaxPerMonth =
      interestCostPerMonth - MAX_FULL_DEDUCTABLE_AMOUNT_PER_MONTH
    return Math.round(
      MAX_FULL_DEDUCTABLE_AMOUNT_PER_MONTH * 0.7 + amountAboveMaxPerMonth * 0.79
    )
  }
  return Math.round(interestCostPerMonth * 0.7)
}

export function getMaxDeposit(propertyValue: number, minMortgage: number) {
  return Math.max(propertyValue - minMortgage, 0)
}

export function getMinDeposit(
  propertyValue: number,
  maxPersonalLoan: number,
  minPersonalLoan: number,
  maxMortgage: number,
  minPropertyValue: number
) {
  if (propertyValue > minPropertyValue + minPersonalLoan) {
    return Math.max(
      propertyValue - maxMortgage,
      propertyValue * 0.15 - maxPersonalLoan,
      propertyValue * 0.05
    )
  } else {
    return Math.ceil(propertyValue * 0.15)
  }
}

/**
 * We are using Math.floor to ensure that we don't say there is enough deposit when it is not.
 */
export function getPercentageValues(
  propertyValue: number,
  downPayment: number,
  personalLoanAmount: number
) {
  const unroundedDownPaymentPercentage =
    (downPayment / propertyValue) * 100 || 0
  const unroundedPersonalLoanPercentage =
    (personalLoanAmount / propertyValue) * 100 || 0
  const downPaymentPercentage =
    Math.floor(unroundedDownPaymentPercentage * 10) / 10
  const personalLoanPercentage =
    Math.floor(unroundedPersonalLoanPercentage * 10) / 10
  return {
    downPaymentPercentage,
    personalLoanPercentage,
  }
}

export function getMonthlyPersonalLoanAmortization(
  personalLoanAmount: number,
  personalLoanLengthInYears = 10
) {
  const months = 12
  return Math.round(personalLoanAmount / months / personalLoanLengthInYears)
}

export function getCalculationExample(
  amortization: number,
  initialLoanAmount = 0,
  mortgageRate = 0,
  years = 0
) {
  const months = years * 12
  const monthlyAmortization =
    amortization !== undefined ? amortization : initialLoanAmount / months
  let totalMortgageRate = 0
  let totalAmortization = 0
  let loanAmount = initialLoanAmount

  for (let i = 0; months > i; i++) {
    const monthlymortgageRate = (loanAmount * (mortgageRate / 100)) / 12
    totalAmortization += monthlyAmortization
    totalMortgageRate += monthlymortgageRate
    loanAmount -= monthlyAmortization
  }

  return {
    totalLoanCost: Math.round(totalAmortization + totalMortgageRate),
    totalAmortization: Math.round(totalAmortization),
    totalInterestCost: Math.round(totalMortgageRate),
    remainingAmount: Math.round(loanAmount),
  }
}

export function getInitialPersonalLoanAmount(
  propertyValue: number,
  downPayment: number,
  minAmountForRedemptionOfLoan: number
) {
  const missingAmount = propertyValue * 0.15 - downPayment
  if (missingAmount >= minAmountForRedemptionOfLoan) return missingAmount
  return minAmountForRedemptionOfLoan
}

export function getEssentialDerivedState(
  downPayment: number,
  propertyValue: number,
  personalLoanIsAdded: boolean,
  personalLoanAmount: number
) {
  const { downPaymentPercentage, personalLoanPercentage } = getPercentageValues(
    propertyValue,
    downPayment,
    personalLoanAmount
  )
  const downPaymentIsEnough = downPaymentPercentage >= 15
  const effectivePersonalLoanAmount =
    personalLoanIsAdded && !downPaymentIsEnough ? personalLoanAmount : 0
  const effectivePersonalLoanPercentage =
    personalLoanIsAdded && !downPaymentIsEnough ? personalLoanPercentage : 0
  const totalIsEnoughForPurchase =
    downPaymentPercentage + effectivePersonalLoanPercentage >= 15
  const showPersonalLoan = !downPaymentIsEnough
  return {
    effectivePersonalLoanAmount,
    showPersonalLoan,
    totalIsEnoughForPurchase,
    downPaymentPercentage,
    personalLoanPercentage,
  }
}
